import InfoRounded from '@mui/icons-material/InfoRounded';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { NameCell } from '@openx/components-core/lib/NameCell/NameCell';
import { StatusIndicator } from '@openx/components-core/lib/StatusIndicator/StatusIndicator';
import { Table, TableName } from '@openx/components-table/Table';
import { dealStatusForm } from '@openx/types/deal';
import type { TableColumn } from '@openx/types/tableHelpers';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const StyledContainerCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const StyledSubtitleTypography = styled(Typography)`
  margin-left: ${({ theme }) => theme.spacing(3)};
` as typeof Typography;

const StyledCardHeader = styled(CardHeader)`
  .MuiCardHeader-action {
    margin-right: 0;
    margin-top: 0;
  }
  & .MuiTypography-root {
    display: flex;
  }

  .MuiCardHeader-title {
    padding-left: ${({ theme }) => theme.spacing(2.5)};
  }
`;

const StyledHiddenDealsTypography = styled(StyledSubtitleTypography)`
  display: inline-flex;
`;

export type UsedByEntryItem = {
  deal_id?: string | null;
  id: string;
  uid: string;
  name: string;
  status: string;
  start_date?: string | null;
  end_date?: string | null;
};

export type UsedByEntryProps = {
  entries: UsedByEntryItem[];
  showMore: () => void;
  hasMore: boolean;
  title: string;
  idRowTitle: string;
  getPathToDetails: (id: string) => string;
  isOutsideLink?: boolean;
  isFetchingMore: boolean;
  dataTest: string;
  hiddenEntriesNumber?: number;
  entriesNumber: number;
};

export function UsedByEntry({
  entries,
  showMore,
  hasMore,
  title,
  idRowTitle,
  getPathToDetails,
  isFetchingMore,
  dataTest,
  isOutsideLink = false,
  hiddenEntriesNumber = 0,
  entriesNumber,
}: UsedByEntryProps) {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);

  const toggleShowTable = useCallback(() => setShowTable(!showTable), [showTable]);

  const columnDefs: TableColumn<UsedByEntryItem>[] = useMemo(
    () => [
      {
        key: 'name',
        disabled: true,
        render: entry => {
          const dealId = entry.deal_id || entry.id;
          const MAX_LENGTH = 70;

          return (
            <NameCell
              id={dealId.length > MAX_LENGTH ? `${dealId.substring(0, MAX_LENGTH)}...` : dealId}
              subRowLabel={idRowTitle}
              name={entry.name ?? ''}
              pathTo={getPathToDetails(entry.uid)}
              isOutsideLink={isOutsideLink}
            />
          );
        },
        title: t('Name'),
        width: '55%',
      },
      {
        key: 'status',
        render: data => <StatusIndicator status={dealStatusForm[data.status]} label={data.status} />,
        title: t('Status'),
        width: '15%',
      },
      {
        isDate: true,
        isExactDate: true,
        key: 'start_date',
        title: t('Start Date'),
        width: '15%',
      },
      {
        isDate: true,
        isExactDate: true,
        key: 'end_date',
        title: t('End Date'),
        width: '15%',
      },
    ],
    [getPathToDetails, idRowTitle, isOutsideLink, t]
  );

  const dealPluralityLabel = hiddenEntriesNumber > 1 ? t('deals') : t('deal');

  return (
    <StyledContainerCard data-test={dataTest}>
      <StyledCardHeader
        title={
          <Typography variant="h3" data-test="used-by-title">
            {title}
            <StyledSubtitleTypography
              variant="body1"
              component="span"
              color="textSecondary"
              data-test="used-by-number-of-entries"
            >
              {hasMore ? `${entriesNumber}+` : entriesNumber}
            </StyledSubtitleTypography>
            {hiddenEntriesNumber > 0 && !hasMore && (
              <StyledHiddenDealsTypography variant="body1" data-test="used-by-number-of-hidden-entries">
                <IconButton size="small">
                  <InfoRounded />
                </IconButton>
                {t(
                  `Package is also used by ${hiddenEntriesNumber} ${dealPluralityLabel} but you don't have permission to see them.`
                )}
              </StyledHiddenDealsTypography>
            )}
          </Typography>
        }
        disableTypography
        action={
          !!entries.length && (
            <Button variant="text" color="primary" onClick={toggleShowTable} data-test="used-by-show-button">
              {showTable ? t('Hide') : t('Show')}
            </Button>
          )
        }
      />
      {showTable && (
        <Table data={entries} columns={columnDefs} loading={false} stickyTable={false} name={TableName.USED_BY_ENTRY} />
      )}
      {showTable && hasMore && (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme => theme.spacing(1),
          }}
        >
          <Button onClick={showMore} variant="text" loading={isFetchingMore} color="primary">
            {t('Show More')}
          </Button>
        </Grid>
      )}
    </StyledContainerCard>
  );
}
